import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { DashboardVisitorIntelWeeklySummaryResponseDto } from 'src/app/shared/dto/dashboard-visitor-intel-weekly-summary-response.dto';
import { DatePipe } from '@angular/common';
import { Observable } from 'rxjs';
import { DataTableColumn } from 'src/app/shared/components';
import { Router } from '@angular/router';
import { ModalNavigationEnum } from 'src/app/shared/constants';
import { AddonsIdentifiersEnum, FeatureFlagsEnum, ResultTabsetUrlsEnum } from 'src/app/shared/enums';
import { UniversalSearchModel, UniversalSearchModelType } from "src/app/core/models/universal-search.model";
import { REQUEST_TYPE } from "src/app/shared/enums";
import { cloneDeep } from 'lodash-es';
import { AuthenticationService, UserService } from 'src/app/core/services';
import {
  DashboardVisitorIntelAgentsResponseDto,
  DashboardVisitorIntelAllWeeklyVisitedCounts,
  DashboardVisitorIntelWeeklyExportedCount,
  DashboardVisitorIntelWeeklyVisitedCompaniesResponseDto,
  DashboardVisitorIntelWeeklyVisitedCount
} from 'src/app/shared/dto';
import { RequestBodyVisitorInsight } from 'src/app/core/models/request-body.model';
import { UntilDestroy } from '@ngneat/until-destroy';
import moment from 'moment';

@UntilDestroy()
@Component({
    selector: 'app-visitor-intel-card',
    templateUrl: './visitor-intel-card.component.html',
    styleUrls: ['./visitor-intel-card.component.scss', '../../common-reporting-dashboard.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DatePipe],
    standalone: false
})
export class VisitorIntelCardComponent implements OnChanges {
  @Input() isLoading: boolean;
  @Input() isAdmin: boolean;

  // POR-11002 Track-history single-card mode: hide the dropdown + preselect this agent.
  @Input() hideAgentSelector: boolean = false;
  @Input() preselectedAgentId: string | number | null = null;
  private preselectApplied: boolean = false;
  @Input() set visitorIntelData(
    value: DashboardVisitorIntelWeeklySummaryResponseDto[],
  ) {
    if (value) {
      this.innerVisitorIntelWeeklyData = value;
      (!this.innerVisitorIntelAgentsData || this.innerVisitorIntelAgentsData?.length === 0) ? this.updateChartData() : null;
    }
  }

  get visitorIntelData(): DashboardVisitorIntelWeeklySummaryResponseDto[] {
    return this.innerVisitorIntelWeeklyData;
  }

  @Input() set visitorIntelAgents(
    value: DashboardVisitorIntelAgentsResponseDto[],
  ) {
    if (value) {
      this.innerVisitorIntelAgentsData = value;
    }
  }

  get visitorIntelAgents(): DashboardVisitorIntelAgentsResponseDto[] {
    return this.innerVisitorIntelAgentsData;
  }

  @Input() isVisitorIntelSetupDone: boolean;

  @Input() set visitorIntelAllWeeklyExportedData(
    value: DashboardVisitorIntelAllWeeklyVisitedCounts[]
  ) {
    if (value) {
        this.innerVisitorIntelAllWeeklyExportedData = value;
    }
  }

  get visitorIntelAllWeeklyExportedData(): DashboardVisitorIntelAllWeeklyVisitedCounts[] {
    return this.innerVisitorIntelAllWeeklyExportedData;
  }

  @Input() chartData: Record<string, number[]> | number[];
  @Input() chartDataLabels: string[];
  @Input() tableData: DashboardVisitorIntelWeeklyExportedCount[] | DashboardVisitorIntelWeeklyVisitedCount[];
  @Input() isWeeklyAgentsNotRun: boolean = false;
  @Input() viewAllData: {universal: boolean, automation_id: number, saved_search_id: number};
  @Input() isAllAgentsEmailOnly: boolean;
  @Input() isAllAgentsExportOnly: boolean;

  @Output() searchTrigger = new EventEmitter();
  @Output() runVIAgent = new EventEmitter();
  @Output() agentSelectionChanged = new EventEmitter();
  @Output() saveSearchTrigger = new EventEmitter();

  columns: DataTableColumn[] = [
    {
      dataField: 'event_date',
      caption: 'Date',
      cellTemplate: 'dateTemplate',
      width: '85%',
      cellClasses: 'border-b-0 pb-1 px-4',
      headerCellClasses: 'py-1',
      dateFormat: 'MM/dd/yy'
    },
    {
      dataField: 'total_company_count',
      caption: 'Visitors',
      cellTemplate: 'numberTemplate',
      width: '15%',
      cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
      headerCellClasses: 'py-1',
    },
  ];

    exportColumns: DataTableColumn[] = [
        {
            dataField: 'job_date',
            caption: 'Date',
            cellTemplate: 'dateTemplate',
            width: '40%',
            cellClasses: 'border-b-0 pb-1 px-4',
            headerCellClasses: 'py-1',
            dateFormat: 'MM/dd/yy'
        },
        {
            dataField: 'companies_exported_count',
            caption: 'Companies',
            cellTemplate: 'numberTemplate',
            width: '30%',
            cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
            headerCellClasses: 'py-1',
        },
        {
            dataField: 'contacts_exported_count',
            caption: 'Contacts',
            cellTemplate: 'numberTemplate',
            width: '30%',
            cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
            headerCellClasses: 'py-1',
        }
    ];

    visitorColumns: DataTableColumn[] = [
        {
            dataField: 'job_date',
            caption: 'Date',
            cellTemplate: 'dateTemplate',
            width: '50%',
            cellClasses: 'border-b-0 pb-1 px-4',
            headerCellClasses: 'py-1',
            dateFormat: 'MM/dd/yy'
        },
        {
            dataField: 'visited_company_count',
            caption: 'Companies',
            cellTemplate: 'numberTemplate',
            width: '50%',
            cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
            headerCellClasses: 'py-1',
        },
    ];

    emailOnlyColumns: DataTableColumn[] = [
        {
            dataField: 'job_date',
            caption: 'Date',
            cellTemplate: 'dateTemplate',
            width: '50%',
            cellClasses: 'border-b-0 pb-1 px-4',
            headerCellClasses: 'py-1',
            dateFormat: 'MM/dd/yy'
        },
        {
            dataField: 'all_companies_count',
            caption: 'Companies',
            cellTemplate: 'numberTemplate',
            width: '50%',
            cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
            headerCellClasses: 'py-1',
        },
    ];

    exportOnlyColumns: DataTableColumn[] = [
        {
            dataField: 'job_date',
            caption: 'Date',
            cellTemplate: 'dateTemplate',
            width: '50%',
            cellClasses: 'border-b-0 pb-1 px-4',
            headerCellClasses: 'py-1',
            dateFormat: 'MM/dd/yy'
        },
        {
            dataField: 'companies_exported_count',
            caption: 'Companies Exported',
            cellTemplate: 'numberTemplate',
            width: '30%',
            allowSorting: false,
            cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
            headerCellClasses: 'py-1 text-center',
        },
        {
            dataField: 'contacts_exported_count',
            caption: 'Contacts Exported',
            cellTemplate: 'numberTemplate',
            width: '30%',
            allowSorting: false,
            cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
            headerCellClasses: 'py-1 text-center',
        },
    ];

    allColumns: DataTableColumn[] = [
        {
            dataField: 'job_date',
            caption: 'Date',
            cellTemplate: 'dateTemplate',
            width: '50%',
            cellClasses: 'border-b-0 pb-1 px-4',
            headerCellClasses: 'py-1',
            dateFormat: 'MM/dd/yy'
        },
        {
            dataField: 'companies_exported_count',
            caption: 'Companies Exported',
            cellTemplate: 'numberTemplate',
            width: '30%',
            allowSorting: false,
            cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
            headerCellClasses: 'py-1 text-center',
        },
        {
            dataField: 'contacts_exported_count',
            caption: 'Contacts Exported',
            cellTemplate: 'numberTemplate',
            width: '30%',
            allowSorting: false,
            cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
            headerCellClasses: 'py-1 text-center',
        },
        {
            dataField: 'all_companies_count',
            caption: 'All Companies',
            cellTemplate: 'numberTemplate',
            width: '30%',
            allowSorting: false,
            cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
            headerCellClasses: 'py-1 text-center',
        },
    ];

  isShowChart: boolean = true;
  isAdmin$: Observable<boolean>;

  selectedOption: DashboardVisitorIntelAgentsResponseDto | { isAllAgents: true } = { isAllAgents: true };
  requestBodyCombined = new RequestBodyVisitorInsight();
  visitorIntelAgentsData$: Observable<DashboardVisitorIntelWeeklyVisitedCompaniesResponseDto>;
  private innerVisitorIntelWeeklyData: DashboardVisitorIntelWeeklySummaryResponseDto[];
  private innerVisitorIntelAgentsData: DashboardVisitorIntelAgentsResponseDto[];
  private innerVisitorIntelAllWeeklyExportedData: DashboardVisitorIntelAllWeeklyVisitedCounts[];

  readonly addonsIdentifiersEnum: typeof AddonsIdentifiersEnum =
    AddonsIdentifiersEnum;
  readonly featureFlagsEnum: typeof FeatureFlagsEnum = FeatureFlagsEnum;
  readonly resultTabsetUrlsEnum: typeof ResultTabsetUrlsEnum = ResultTabsetUrlsEnum;
  readonly modalNavigationEnum: typeof ModalNavigationEnum =
    ModalNavigationEnum;

  constructor(
    private readonly datePipe: DatePipe,
    private readonly router: Router,
    private readonly authenticationService: AuthenticationService,
    private readonly userService: UserService
  ) { }

  ngOnChanges(): void {
    // Preselect once the agent list is available (emits agentSelectionChanged for the orchestrator).
    if (this.preselectApplied || this.preselectedAgentId == null) return;
    const agents: DashboardVisitorIntelAgentsResponseDto[] = this.innerVisitorIntelAgentsData;
    if (!agents?.length) return;
    const match: DashboardVisitorIntelAgentsResponseDto | undefined =
      agents.find((a) => String((a as { id?: unknown })?.id) === String(this.preselectedAgentId));
    if (match) {
      this.preselectApplied = true;
      this.updateSelectedOption(match);
    }
  }

  updateChartData(): void {
    this.chartData = this.innerVisitorIntelWeeklyData?.map(
      (d) => d.total_company_count,
    ).reverse();
    this.chartDataLabels = this.innerVisitorIntelWeeklyData
      ?.map((d) => {
        return this.datePipe.transform(d.event_date, 'MM/dd/yy');
      })
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  }

  onOpenSettings(modal: ModalNavigationEnum): void {
    this.router.navigate(['/', { outlets: { modal: [modal] } }]);
  }

    viewAllPastWeekVisitorIntelData(): void {
        const viDateFromIndex = this.innerVisitorIntelWeeklyData?.length - 1;
        const paramsToUpdate = {
            viDateFrom: moment(this.innerVisitorIntelWeeklyData[viDateFromIndex]?.event_date, "YYYY-MM-DD").format("DD/MM/YYYY"),
            viDateTo: moment(this.innerVisitorIntelWeeklyData[0]?.event_date, "YYYY-MM-DD").format("DD/MM/YYYY"),
            viSearchDurationType: 'CUSTOM',
            viUUID: this.userService.getUserAccountUUID(),
        }
        if (this.visitorIntelAgents?.length !== 0 && this.viewAllData && !this.viewAllData.universal) {
            this.saveSearchTrigger.emit({paramsToUpdate, saved_search_id: this.viewAllData.saved_search_id});
        } else {
            const searchModelData: UniversalSearchModelType = {
                ...cloneDeep(UniversalSearchModel),
                ...paramsToUpdate,
                request_type: REQUEST_TYPE.COMPANIES,
                page: 1,
                verified: 1,
                tiers: this.authenticationService.getHumanVerifiedTiers().toString(),
            };
            this.searchTrigger.emit(searchModelData);
        }
    }

  updateSelectedOption(value: DashboardVisitorIntelAgentsResponseDto | null) {
    this.selectedOption = value;
    this.agentSelectionChanged.emit(value);
  }

  runAgent(): void {
        if ('id' in this.selectedOption) {
            this.runVIAgent.emit(this.selectedOption.id)
        }
    }

}
